/** @jsxImportSource @emotion/react */
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ToggleButtonGroup, ToggleButton, Stack } from '@mui/material';

import AddressSearchPanel from '../AddressSearchPanel';
import FileUploadPanel from 'features/geocoder/components/FileUploadPanel';
import HistoryPreviewList from 'features/geocoder/components/history/HistoryPreviewList';
import FileUploadHistoryList from 'features/geocoder/components/history/FileUploadHistoryList';
import { style as userDataPanelStyles } from 'features/userData/style';

export default function GeocoderControlPanel({
    panelMode,
    setPanelMode,
    geocodingTypeOptions,
    coordSystemOptions,
    encodingOptions,
    delimiterOptions,
    downloadOptions,
    handleResultRecord,
    addLayerFromGeoJSON,
    manualHistoryRecords,
    fileHistoryMeta,
    onHistoryRecordSelect,
    isFileDeletedAfterUpload,
    onClearFileLayers,
}) {

    // 주소/좌표 탭 상태
    const [showReverse, setShowReverse] = useState(false);

    return (
        <>
            <div>
                <div css={[userDataPanelStyles.geocoderHeader]}>
                    <span css={userDataPanelStyles.layerTitle}>주소 찾기</span>
                </div>
                <ToggleButtonGroup
                    orientation="horizontal"
                    exclusive
                    value={panelMode}
                    onChange={(_, newValue) => {
                        if (newValue) {
                            setPanelMode(newValue);
                        }
                    }}
                    sx={{
                        width: '100%',
                        border: 'none', 
                        borderBottom: '1px solid #d6dae8',
                        borderRadius: 0,
                        marginBottom: '5px',
                        '& .MuiToggleButton-root': {
                            flex: 1,
                            border: 'none',
                            borderRadius: 0,
                            fontWeight: 600,
                            fontSize: '16px',
                            textTransform: 'none',
                            py: 0.5, // 탭 높이를 위해 패딩 설정
                            borderBottom: '5px solid transparent',
                            color: '#3c4257',
                            '&.Mui-selected': {
                                color: '#002755', 
                                bgcolor: 'transparent', 
                                borderBottom: '5px solid #002755',
                                '&:hover': {
                                    bgcolor: 'rgba(0, 39, 85, 0.04)',
                                }
                            },
                        },
                    }}
                >
                    <ToggleButton value="1">
                        데이터 직접 입력
                    </ToggleButton>
                    <ToggleButton value="2">
                        파일 업로드
                    </ToggleButton>
                </ToggleButtonGroup>
            </div>

            <div className="layer-body">
                <div className="section">
                    <div style={{ display: panelMode === '1' ? 'block' : 'none' }}>
                        <AddressSearchPanel
                            geocodingTypeOptions={geocodingTypeOptions}
                            coordSystemOptions={coordSystemOptions}
                            onResultRecord={handleResultRecord}
                            downloadOptions={downloadOptions}
                            showReverse={showReverse}
                            setShowReverse={setShowReverse}
                            />
                    </div>
                    <div style={{ display: panelMode === '2' ? 'block' : 'none' }}>
                        <FileUploadPanel
                            encodingOptions={encodingOptions}
                            delimiterOptions={delimiterOptions}
                            geocodingTypeOptions={geocodingTypeOptions}
                            coordSystemOptions={coordSystemOptions}
                            onLayerAdd={addLayerFromGeoJSON}
                            showReverse={showReverse}
                            setShowReverse={setShowReverse}
                            isFileDeletedAfterUpload={isFileDeletedAfterUpload}
                            onClearFileLayers={onClearFileLayers}
                        />
                    </div>
                </div>

                <div className="section" style={{ marginBottom: 20 }}>
                    <div className="section-header">
                        <Stack spacing={2}>
                            <div style={{ display: panelMode !== '2' ? 'block' : 'none' }}>
                                <HistoryPreviewList
                                    records={manualHistoryRecords}
                                    onSelectRecord={onHistoryRecordSelect}
                                />
                            </div>

                            <div style={{ display: panelMode === '2' ? 'block' : 'none' }}>
                                <FileUploadHistoryList
                                    entries={fileHistoryMeta}
                                    onSelectRecord={onHistoryRecordSelect}
                                />
                            </div>
                        </Stack>
                    </div>
                </div>
            </div>
        </>
    );
}

GeocoderControlPanel.propTypes = {
    panelMode: PropTypes.string.isRequired,
    setPanelMode: PropTypes.func.isRequired,
    geocodingTypeOptions: PropTypes.array,
    coordSystemOptions: PropTypes.array,
    encodingOptions: PropTypes.array,
    delimiterOptions: PropTypes.array,
    downloadOptions: PropTypes.array,
    handleResultRecord: PropTypes.func,
    addLayerFromGeoJSON: PropTypes.func,
    manualHistoryRecords: PropTypes.array,
    fileHistoryMeta: PropTypes.array,
    onHistoryRecordSelect: PropTypes.func,
    isFileDeletedAfterUpload: PropTypes.bool,
    onClearFileLayers: PropTypes.func,
};
